import { motion } from "framer-motion";
import {
    AlertTriangle,
    ArrowLeft,
    CheckCircle,
    Chrome,
    Clock,
    Filter,
    Globe2,
    History,
    Laptop,
    Loader2,
    Monitor,
    Search,
    Shield,
    ShieldAlert,
    Smartphone,
    Tablet,
    UserX,
    Users,
    XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { initials, timeAgo } from "../lib/format";
import { goBack } from "../lib/navigation";

const PERIODS = [
    { value: 1, label: "24h" },
    { value: 7, label: "7 jours" },
    { value: 30, label: "30 jours" },
    { value: 90, label: "3 mois" },
];

const ROLE_LABEL = {
    admin: "Admin",
    pre_recruteur: "Pré-recruteur",
    referent: "Référent terrain",
};

function DeviceIcon({ device, className = "h-4 w-4" }) {
    if (!device) return <Monitor className={className} />;
    const d = device.toLowerCase();
    if (d.includes("iphone") || d.includes("mobile")) return <Smartphone className={className} />;
    if (d.includes("ipad") || d.includes("tablet")) return <Tablet className={className} />;
    return <Laptop className={className} />;
}

function KpiTile({ label, value, suffix, icon: Icon, tone = "orange", subtle }) {
    const tones = {
        orange: "from-shariti-orange to-shariti-orange-light text-white",
        emerald: "from-emerald-500 to-emerald-400 text-white",
        red: "from-red-500 to-red-400 text-white",
        slate: "from-slate-700 to-slate-500 text-white",
        amber: "from-amber-500 to-amber-400 text-white",
    };
    return (
        <div className="card-soft p-4 md:p-5" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {label}
                    </div>
                    <div className="mt-1 flex items-baseline gap-1">
                        <span className="text-3xl md:text-4xl font-black tracking-tight text-shariti-black tabular-nums">
                            {value}
                        </span>
                        {suffix && <span className="text-base font-extrabold text-slate-400">{suffix}</span>}
                    </div>
                    {subtle && (
                        <div className="mt-1 text-[11px] font-semibold text-slate-500">{subtle}</div>
                    )}
                </div>
                <div className={`h-10 w-10 rounded-2xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center shrink-0 shadow-btn`}>
                    <Icon className="h-5 w-5" />
                </div>
            </div>
        </div>
    );
}

function UserCard({ summary }) {
    const isActive = summary.login_count > 0;
    const isAdmin = summary.role === "admin";
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-soft p-4 md:p-5"
            data-testid={`summary-card-${summary.user_id}`}
        >
            <div className="flex items-start gap-3">
                {summary.avatar_url ? (
                    <img src={summary.avatar_url} alt="" className="h-11 w-11 rounded-2xl object-cover" />
                ) : (
                    <div className={`h-11 w-11 rounded-2xl ${isAdmin ? "bg-shariti-orange-soft text-shariti-orange" : "bg-blue-100 text-blue-700"} font-extrabold flex items-center justify-center`}>
                        {initials(summary.name || summary.email)}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-extrabold text-shariti-black truncate">
                            {summary.name || summary.email || "—"}
                        </div>
                        <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${
                            isAdmin
                                ? "bg-shariti-orange-soft text-shariti-orange border-shariti-orange/30"
                                : "bg-blue-50 text-blue-700 border-blue-200"
                        }`}>
                            {ROLE_LABEL[summary.role] || summary.role}
                        </span>
                        {!isActive && (
                            <span className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                                <UserX className="h-2.5 w-2.5 inline mr-0.5" />
                                jamais connecté
                            </span>
                        )}
                    </div>
                    <div className="text-[11px] font-semibold text-slate-400 truncate mt-0.5">
                        {summary.email}
                    </div>
                </div>
                {isActive ? (
                    <div className="text-right shrink-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Dernière connexion</div>
                        <div className="text-xs font-extrabold text-shariti-black">
                            {timeAgo(summary.last_login_at)}
                        </div>
                    </div>
                ) : null}
            </div>

            {isActive && (
                <>
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Connexions</div>
                            <div className="mt-0.5 text-xl font-black text-shariti-black tabular-nums">{summary.login_count}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Échecs</div>
                            <div className={`mt-0.5 text-xl font-black tabular-nums ${
                                summary.failed_count > 0 ? "text-shariti-red" : "text-slate-400"
                            }`}>
                                {summary.failed_count}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">IPs uniques</div>
                            <div className="mt-0.5 text-xl font-black text-shariti-black tabular-nums">{summary.unique_ip_count}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Appareil</div>
                            <div className="mt-0.5 inline-flex items-center gap-1.5 text-sm font-extrabold text-shariti-black">
                                <DeviceIcon device={summary.last_device} className="h-3.5 w-3.5 text-slate-500" />
                                {summary.last_device || "—"}
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-shariti-gray/60 text-[11px] font-semibold text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                            <Chrome className="h-3.5 w-3.5 text-slate-400" />
                            {summary.last_browser || "—"}
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1.5">
                            <Monitor className="h-3.5 w-3.5 text-slate-400" />
                            {summary.last_os || "—"}
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1.5 tabular-nums">
                            <Globe2 className="h-3.5 w-3.5 text-slate-400" />
                            <code className="bg-shariti-cream px-1.5 py-0.5 rounded text-[10px]">{summary.last_ip || "—"}</code>
                        </span>
                    </div>
                </>
            )}
        </motion.div>
    );
}

function EventRow({ event }) {
    return (
        <div
            className={`p-3 md:p-4 grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_1fr_auto] gap-3 md:gap-4 items-start md:items-center ${
                event.success ? "" : "bg-red-50/40"
            }`}
            data-testid={`event-${event.id}`}
        >
            <div className="flex items-center gap-2 min-w-0">
                {event.success ? (
                    <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                ) : (
                    <XCircle className="h-4 w-4 text-shariti-red shrink-0" />
                )}
                <div className="min-w-0">
                    <div className="font-extrabold text-shariti-black truncate text-sm">
                        {event.user_name || event.name || event.email || "—"}
                    </div>
                    <div className="text-[10px] font-semibold text-slate-400 truncate">
                        {event.email}
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold text-slate-600 min-w-0">
                <DeviceIcon device={event.device} className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="truncate">{event.device}</span>
                <span className="text-slate-300">·</span>
                <span className="truncate">{event.browser}</span>
                <span className="text-slate-300">·</span>
                <span className="truncate">{event.os}</span>
            </div>
            <div className="text-[11px] font-semibold text-slate-600 tabular-nums inline-flex items-center gap-1.5">
                <Globe2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <code className="bg-shariti-cream px-1.5 py-0.5 rounded text-[10px]">{event.ip || "—"}</code>
            </div>
            <div className="text-[11px] font-bold text-slate-500 tabular-nums inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                {timeAgo(event.at)}
            </div>
            <div className="text-right shrink-0">
                {event.success ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        OK
                    </span>
                ) : (
                    <span
                        className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-50 text-shariti-red border border-red-200"
                        title={event.failure_reason}
                    >
                        Échec {event.failure_reason === "unknown_email" ? "· email inconnu" : event.failure_reason === "bad_password" ? "· mot de passe" : ""}
                    </span>
                )}
            </div>
        </div>
    );
}

export default function LoginHistory() {
    const navigate = useNavigate();
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [onlyFailures, setOnlyFailures] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function fetchAll() {
            setLoading(true);
            try {
                const [s, e] = await Promise.all([
                    api.get("/admin/login-history/summary", { params: { days } }),
                    api.get("/admin/login-history", {
                        params: { days, limit: 200, ...(onlyFailures ? { success: false } : {}) },
                    }),
                ]);
                if (!cancelled) {
                    setSummary(s.data);
                    setEvents(e.data.events || []);
                }
            } catch (_err) {
                if (!cancelled) {
                    setSummary(null);
                    setEvents([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchAll();
        return () => { cancelled = true; };
    }, [days, onlyFailures]);

    const filteredEvents = useMemo(() => {
        if (!search) return events;
        const q = search.toLowerCase();
        return events.filter((e) => (
            (e.email || "").toLowerCase().includes(q)
            || (e.user_name || e.name || "").toLowerCase().includes(q)
            || (e.ip || "").includes(q)
            || (e.browser || "").toLowerCase().includes(q)
            || (e.os || "").toLowerCase().includes(q)
        ));
    }, [events, search]);

    const filteredSummary = useMemo(() => {
        if (!summary?.users) return [];
        if (!search) return summary.users;
        const q = search.toLowerCase();
        return summary.users.filter((u) => (
            (u.email || "").toLowerCase().includes(q)
            || (u.name || "").toLowerCase().includes(q)
            || (u.last_ip || "").includes(q)
        ));
    }, [summary, search]);

    return (
        <div className="space-y-5 md:space-y-7" data-testid="login-history-page">
            <button
                onClick={() => goBack(navigate, "/dashboard")}
                data-testid="login-history-back"
                className="inline-flex items-center gap-1.5 bg-white text-slate-600 border border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange rounded-full font-extrabold px-3 md:px-4 py-2 text-xs md:text-sm transition"
            >
                <ArrowLeft className="h-4 w-4" />
                Retour
            </button>

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest mb-2">
                        <Shield className="h-3 w-3" /> Audit · admin
                    </div>
                    <h1 className="text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Historique de connexion
                    </h1>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                        Qui s'est connecté, quand, d'où — vue d'ensemble de l'activité de votre équipe.
                    </p>
                </div>
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white border border-shariti-gray shrink-0">
                    {PERIODS.map((p) => (
                        <button
                            key={p.value}
                            onClick={() => setDays(p.value)}
                            data-testid={`period-${p.value}`}
                            className={`px-3 h-8 rounded-full text-xs font-extrabold transition ${
                                days === p.value
                                    ? "bg-shariti-orange text-white shadow-btn"
                                    : "text-slate-600 hover:text-shariti-orange"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading && !summary ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-shariti-orange" />
                </div>
            ) : !summary ? (
                <div className="card-soft p-6 text-center text-shariti-red font-bold">
                    Impossible de charger l'historique
                </div>
            ) : (
                <>
                    {/* === Aggregated KPIs === */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        <KpiTile
                            label="Utilisateurs actifs"
                            value={summary.active_users}
                            suffix={`/${summary.total_users}`}
                            icon={Users}
                            tone="emerald"
                            subtle={`${summary.total_users - summary.active_users} jamais connecté${(summary.total_users - summary.active_users) > 1 ? "s" : ""}`}
                        />
                        <KpiTile
                            label="Connexions réussies"
                            value={summary.total_successful_logins}
                            icon={CheckCircle}
                            tone="orange"
                            subtle={`sur ${days} jour${days > 1 ? "s" : ""}`}
                        />
                        <KpiTile
                            label="Tentatives échouées"
                            value={summary.total_failed_logins}
                            icon={ShieldAlert}
                            tone="red"
                            subtle={summary.failed_unknown_email > 0 ? `dont ${summary.failed_unknown_email} email inconnu` : "aucun email inconnu"}
                        />
                        <KpiTile
                            label="Événements bruts"
                            value={events.length}
                            icon={History}
                            tone="slate"
                            subtle="dernières lignes affichées"
                        />
                    </div>

                    {/* === Search bar + filters === */}
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex items-center gap-2 bg-white border border-shariti-gray rounded-full px-4 h-11 text-sm flex-1 max-w-lg">
                            <Search className="h-4 w-4 text-slate-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Nom, email, IP, navigateur…"
                                data-testid="login-history-search"
                                className="bg-transparent outline-none flex-1 text-shariti-black font-medium"
                            />
                        </div>
                        <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={onlyFailures}
                                onChange={(e) => setOnlyFailures(e.target.checked)}
                                data-testid="filter-only-failures"
                                className="h-4 w-4 rounded border-shariti-gray accent-shariti-red"
                            />
                            <AlertTriangle className="h-3.5 w-3.5 text-shariti-red" />
                            Échecs uniquement
                        </label>
                    </div>

                    {/* === Per-user summary === */}
                    <div>
                        <h2 className="text-xl md:text-2xl font-black text-shariti-black mb-3 flex items-center gap-2">
                            <Users className="h-5 w-5 text-shariti-orange" />
                            Synthèse par utilisateur
                            <span className="ml-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                                {filteredSummary.length}
                            </span>
                        </h2>
                        {filteredSummary.length === 0 ? (
                            <div className="card-soft p-8 text-center text-slate-400 font-medium">
                                Aucun utilisateur ne correspond à votre recherche.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                {filteredSummary.map((u) => (
                                    <UserCard key={u.user_id} summary={u} />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* === Detailed event timeline === */}
                    <div>
                        <h2 className="text-xl md:text-2xl font-black text-shariti-black mb-3 flex items-center gap-2">
                            <Clock className="h-5 w-5 text-shariti-orange" />
                            Journal des connexions
                            <span className="ml-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                                {filteredEvents.length}
                            </span>
                        </h2>
                        <div className="card-soft overflow-hidden">
                            {/* Header row (desktop only) */}
                            <div className="hidden md:grid grid-cols-[2fr_2fr_1fr_1fr_auto] gap-4 px-4 py-2.5 bg-shariti-cream border-b border-shariti-gray text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                <span>Utilisateur</span>
                                <span>Appareil · Navigateur</span>
                                <span>IP</span>
                                <span>Quand</span>
                                <span>État</span>
                            </div>
                            <div className="divide-y divide-shariti-gray/60">
                                {filteredEvents.length === 0 ? (
                                    <div className="p-8 text-center text-slate-400 font-medium">
                                        Aucun événement à afficher pour cette période.
                                    </div>
                                ) : (
                                    filteredEvents.map((e) => <EventRow key={e.id} event={e} />)
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="text-[11px] font-medium text-slate-400 text-center">
                        Les données sont conservées pour des raisons d'audit de sécurité. Les IP sont collectées
                        conformément à la politique de confidentialité. Vos utilisateurs sont informés du suivi.
                    </div>
                </>
            )}
        </div>
    );
}
